import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, UserX, FileWarning, RefreshCw, ChevronRight, TrendingDown, BellOff, Mail } from "lucide-react";
import Header from "@/components/Header";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const SECTIONS = [
  { key: "registered_no_project", label: "Registered, no project (>7d)", icon: UserX, color: "amber" },
  { key: "drafted_not_submitted", label: "Drafted, not submitted (>14d)", icon: FileWarning, color: "orange" },
  { key: "accepted_not_started", label: "Quote accepted, not started (>3d)", icon: Clock, color: "rose" },
  { key: "issued_no_followup", label: "Issued, no follow-up (>30d)", icon: TrendingDown, color: "blue" },
];

export default function AdminDashboard() {
  const [stuckDays, setStuckDays] = useState(7);
  const [stuck, setStuck] = useState({ permits: [], count: 0 });
  const [havent, setHavent] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: h }] = await Promise.all([
        api.get(`/admin/dashboard/stuck-permits?days=${stuckDays}`),
        api.get(`/admin/dashboard/havent-done`),
      ]);
      setStuck(s);
      setHavent(h);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load dashboard");
    } finally { setLoading(false); }
  }, [stuckDays]);

  useEffect(() => { load(); }, [load]);

  const snoozePermit = async (permitId) => {
    const days = window.prompt("Snooze for how many days?", "5");
    if (!days) return;
    const reason = window.prompt("Reason (optional, helps audit trail)", "Waiting on AHJ");
    try {
      await api.post(`/admin/permits/${permitId}/snooze`, { days: Number(days), reason: reason || "" });
      toast.success(`Snoozed ${days} days — resurfaces ${new Date(Date.now() + Number(days) * 86400000).toLocaleDateString()}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not snooze");
    }
  };

  const previewEmail = async (kind) => {
    try {
      await api.post(`/admin/emails/preview`, { kind, send: true });
      toast.success(`Preview email sent to your inbox (${kind})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send preview");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12" data-testid="admin-dashboard-page">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-indigo-600 mb-2">Admin · Pipeline health</div>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-slate-900">Workflow Visibility</h1>
            <p className="text-sm text-slate-500 mt-3 max-w-2xl">
              Where things are stuck, who hasn&apos;t taken the next step, and which engagements need a nudge.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="dashboard-reload">
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`} />Reload
          </Button>
        </div>

        {/* Stuck-stage flags */}
        <section className="mb-12" data-testid="stuck-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="font-outfit text-2xl font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />Stuck permits
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-mono">stuck ≥</span>
              <Input type="number" min="1" max="365" value={stuckDays}
                onChange={(e) => setStuckDays(Number(e.target.value) || 7)}
                className="h-8 w-20 text-sm" data-testid="stuck-days-input" />
              <span className="text-xs text-slate-500 font-mono">days</span>
            </div>
          </div>
          {stuck.count === 0 ? (
            <div className="bg-emerald-50/40 border border-emerald-200 rounded-xl p-6 text-sm text-emerald-700" data-testid="stuck-empty">
              Nothing stuck for ≥ {stuckDays} days. Pipeline is healthy.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-600">
                  <tr><th className="px-4 py-3">Project</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Stuck in</th><th className="px-4 py-3 text-right">Days</th><th className="px-4 py-3"></th></tr>
                </thead>
                <tbody>
                  {stuck.permits.map((p) => (
                    <tr key={p.permit_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`stuck-row-${p.permit_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 truncate">{p.project_name}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{(p.permit_type || "").replace(/_/g, " ")}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="text-slate-900 truncate">{p.email || "—"}</div>
                        {p.has_admin_history && <div className="text-[10px] text-indigo-600 font-mono">CTC-assisted</div>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className="text-[10px] font-mono bg-orange-50 text-orange-700 border-orange-200">
                          {(p.status || "").replace(/_/g, " ")}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-rose-700">{p.days_in_status}d</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => snoozePermit(p.permit_id)}
                            data-testid={`snooze-${p.permit_id}`} title="Snooze 5 days"
                            className="text-xs gap-1 text-amber-700 hover:bg-amber-50 hover:text-amber-900">
                            <BellOff className="w-3 h-3" />Snooze
                          </Button>
                          <Link to={`/permits/${p.permit_id}`} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 px-2">
                            Open<ChevronRight className="w-3 h-3" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Haven't done */}
        {havent && (
          <section data-testid="havent-section">
            <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <UserX className="w-5 h-5 text-rose-600" />Haven&apos;t done yet
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {SECTIONS.map((s) => {
                const items = havent[s.key] || [];
                const Icon = s.icon;
                return (
                  <div key={s.key} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`havent-card-${s.key}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 text-${s.color}-600`} />
                        <h3 className="font-outfit font-bold text-sm text-slate-900">{s.label}</h3>
                      </div>
                      <Badge variant="outline" className={`text-xs font-mono bg-${s.color}-50 text-${s.color}-700 border-${s.color}-200`}>
                        {items.length}
                      </Badge>
                    </div>
                    {items.length === 0 ? (
                      <div className="text-xs text-slate-400 italic">All clear.</div>
                    ) : (
                      <ul className="space-y-2 max-h-64 overflow-auto">
                        {items.slice(0, 10).map((it, i) => (
                          <li key={i} className="text-xs flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0">
                            <div className="min-w-0 flex-1">
                              <div className="text-slate-900 truncate">{it.email || it.contact_email || "—"}</div>
                              <div className="text-[10px] text-slate-500 truncate font-mono">{it.project_name || ""}</div>
                            </div>
                            <span className="text-[10px] font-mono text-slate-500 whitespace-nowrap">
                              {it.days_since_register || it.days_in_draft || it.days_since_accept || it.days_since_issue}d
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Email preview / QA */}
        <section className="mt-12" data-testid="email-preview-section">
          <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-emerald-600" />Email QA · Send a preview to yourself
          </h2>
          <div className="bg-white border border-slate-200 rounded-xl p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            <Button variant="outline" onClick={() => previewEmail("quote_sent")} data-testid="preview-quote-sent"
              className="justify-start gap-2 h-auto py-3 px-4 text-left">
              <Mail className="w-4 h-4 text-indigo-600 flex-none" />
              <div>
                <div className="font-semibold text-sm text-slate-900">Quote sent</div>
                <div className="text-[10px] text-slate-500">Customer-facing quote ready w/ Stripe Pay button</div>
              </div>
            </Button>
            <Button variant="outline" onClick={() => previewEmail("weekly_status")} data-testid="preview-weekly-status"
              className="justify-start gap-2 h-auto py-3 px-4 text-left">
              <Mail className="w-4 h-4 text-emerald-600 flex-none" />
              <div>
                <div className="font-semibold text-sm text-slate-900">Weekly status digest</div>
                <div className="text-[10px] text-slate-500">Customer-facing project recap (7 days)</div>
              </div>
            </Button>
            <Button variant="outline" onClick={() => previewEmail("admin_digest")} data-testid="preview-admin-digest"
              className="justify-start gap-2 h-auto py-3 px-4 text-left">
              <Mail className="w-4 h-4 text-rose-600 flex-none" />
              <div>
                <div className="font-semibold text-sm text-slate-900">Admin digest</div>
                <div className="text-[10px] text-slate-500">Your daily pipeline health email</div>
              </div>
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}
